import { useState, useEffect, useCallback } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, SectionTitle } from "../primitives";

export const BudgetsManager = () => {
  const [budgets, setBudgets] = useState<any[]>([]);
  const [summary, setSummary] = useState<any[]>([]);
  const [bUnits, setBUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState<Record<string, any>>({ business_unit_id: '', fiscal_year: new Date().getFullYear(), budget_amount: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const [b, s, bu] = await Promise.all([
      apiCall(`/spend/budgets?fiscal_year=${fiscalYear}`),
      apiCall(`/spend/summary?fiscal_year=${fiscalYear}`),
      apiCall('/settings/business-units'),
    ]);
    if (b?.budgets) setBudgets(b.budgets);
    if (s?.summary) setSummary(s.summary);
    if (bu?.businessUnits) setBUnits(bu.businessUnits);
    setLoading(false);
  }, [fiscalYear]);
  useEffect(() => { load(); }, [load]);

  const fmtMoney = (v: any) => v == null ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const summaryByBudgetId = Object.fromEntries(summary.map((s: any) => [s.budget_id, s]));

  const add = async () => {
    if (!form.business_unit_id) return alert('Business unit required');
    if (!form.budget_amount || Number(form.budget_amount) < 0) return alert('budget_amount must be >= 0');
    const r = await apiCall('/spend/budgets', {
      method: 'POST',
      body: JSON.stringify({
        business_unit_id: form.business_unit_id,
        fiscal_year: Number(form.fiscal_year),
        budget_amount: Number(form.budget_amount),
        notes: form.notes || null,
      }),
    });
    if (r?.budget) { setForm({ business_unit_id: '', fiscal_year: fiscalYear, budget_amount: '', notes: '' }); load(); }
    else alert(r?.error || 'Failed');
  };

  const editBudget = async (b: any) => {
    const amt = window.prompt(`Budget amount for ${b.business_unit_name} FY${b.fiscal_year}:`, String(b.budget_amount));
    if (amt == null) return;
    if (Number(amt) < 0) return alert('Must be >= 0');
    await apiCall(`/spend/budgets/${b.id}`, { method: 'PUT', body: JSON.stringify({ budget_amount: Number(amt) }) });
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this budget? Spend entries will remain.')) return;
    await apiCall(`/spend/budgets/${id}`, { method: 'DELETE' });
    load();
  };

  if (loading) return <div style={{ padding: 20, color: T.txD }}>Loading budgets...</div>;

  const inputStyle = { padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' as const, width: '100%' };

  return (
    <div>
      <SectionTitle sub="Annual budgets per business unit. Spend entries roll up to parent BUs via the hierarchy.">Budgets</SectionTitle>

      <Card style={{ marginBottom: 12, padding: 12, backgroundColor: T.bg2 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>Fiscal Year</div>
          <input type="number" min={2000} max={2100} value={fiscalYear} onChange={e => setFiscalYear(Number(e.target.value) || new Date().getFullYear())} style={{ ...inputStyle, width: 100 }} />
        </div>
      </Card>

      <Card style={{ marginBottom: 12, padding: 12, backgroundColor: T.bg2 }}>
        <div style={{ fontSize: 11, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 8 }}>New Budget</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Business Unit *</div><select value={form.business_unit_id} onChange={e => setForm((p: any) => ({ ...p, business_unit_id: e.target.value }))} style={inputStyle as any}><option value="">Select...</option>{bUnits.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Fiscal Year *</div><input type="number" min={2000} max={2100} value={form.fiscal_year} onChange={e => setForm((p: any) => ({ ...p, fiscal_year: e.target.value }))} style={inputStyle} /></div>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Budget ($) *</div><input type="number" min={0} step="0.01" value={form.budget_amount} onChange={e => setForm((p: any) => ({ ...p, budget_amount: e.target.value }))} placeholder="0.00" style={inputStyle} /></div>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Notes</div><input value={form.notes} onChange={e => setForm((p: any) => ({ ...p, notes: e.target.value }))} style={inputStyle} /></div>
          <Btn v="primary" sz="sm" onClick={() => { add(); }}>Add Budget</Btn>
        </div>
      </Card>

      <Card style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${T.brd}` }}>
              {['Business Unit', 'FY', 'Budget', 'Spent', 'Remaining', '% Used', 'Notes', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {budgets.map((b: any) => {
              const s = summaryByBudgetId[b.id] || {};
              const pct = s.percent_used;
              const pctColor = pct == null ? T.txD : pct >= 100 ? T.red : pct >= 80 ? T.ylw : T.grn;
              return (
                <tr key={b.id} style={{ borderBottom: `1px solid ${T.brd}` }}>
                  <td style={{ padding: '8px 10px', color: T.tx }}>{b.business_unit_name || <span style={{ color: T.txD }}>(unknown)</span>}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontVariantNumeric: 'tabular-nums' }}>{b.fiscal_year}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.budget_amount)}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(s.spent_amount ?? 0)}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(s.remaining_amount ?? b.budget_amount)}</td>
                  <td style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}><span style={{ color: pctColor, fontWeight: 600 }}>{pct == null ? '—' : `${pct}%`}</span></td>
                  <td style={{ padding: '8px 10px', color: T.txM, fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.notes || ''}</td>
                  <td style={{ padding: '8px 10px', display: 'flex', gap: 8 }}>
                    <button onClick={() => editBudget(b)} style={{ fontSize: 10, color: T.acc, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                    <button onClick={() => remove(b.id)} style={{ fontSize: 10, color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                  </td>
                </tr>
              );
            })}
            {budgets.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: T.txD, fontSize: 12 }}>No budgets for FY{fiscalYear}. Add one above.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
